import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, PaymentOption } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import {
  NoddiAsyncConfirmationDialog,
  NoddiBasicCard,
  NoddiButton,
  NoddiDummyButton,
  NoddiTextArea
} from 'noddi-ui-web';
import {
  CustomDateFormats,
  customerRoutes,
  getBookingTimeDisplayText,
  noddiFormatDate,
  stringToUpperCase
} from 'noddi-util';
import { getLongAddressName, useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const ChangeBooking = () => {
  const { slug } = useParams() as { slug: string };

  const {
    data: booking,
    isPending: isBookingPending,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug }
  });

  if (isBookingPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingError) {
    return <ApiErrorPageWithTrans error={[bookingError]} />;
  }
  return <ChangeBookingScreen booking={booking} />;
};

const ChangeBookingScreen = ({ booking }: { booking: BookingRecordDetailsCustomer }) => {
  const { isLoggedIn } = useAuthContext();
  const navigate = useNavigate();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const [cancellationReason, setCancellationReason] = useState('');
  const deliveryDate = stringToUpperCase(noddiFormatDate(booking.date, CustomDateFormats.FULL_DAY_MONTH));
  const isStationary = booking.locationType === BookingLocationType.STATIONARY;
  const hasPaidWithPayBefore =
    booking.order.paymentOption === PaymentOption.PAY_BEFORE && booking.order.amountPaid.amount > 0;

  const {
    isLoading: isBookingPermissionsLoading,
    data: bookingPermissions,
    error: bookingPermissionsError
  } = noddiAsync.useGet({
    type: URLKeys.getEditBookingPermissions,
    input: { bookingId: booking.id },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  const { mutateAsync: cancelBooking, isPending: isCancelling } = noddiAsync.usePatch({
    type: URLKeys.patchCancelBooking,
    queryConfig: {
      onSuccess: () => {
        navigate(customerRoutes.cancelledBooking.getPath());
      }
    }
  });

  if (isBookingPermissionsLoading) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingPermissionsError) {
    return <ApiErrorPageWithTrans error={bookingPermissionsError} />;
  }

  return (
    <ContentWrapperCustomerApp title={t`Edit booking`}>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-4'>
          {!isStationary && (
            <NoddiBasicCard
              {...(bookingPermissions?.canEditAddress && {
                // oxlint-disable-next-line typescript/no-misused-promises -- NoddiBasicCard onClick is () => void; navigate() returns a promise that only tracks lazy-route loading
                onClick: () => navigate(customerRoutes.editBookingAddress.getPath({ slug: booking.slug }))
              })}
            >
              <div className='flex flex-col gap-2'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <NoddiIcon name='MapPin' />
                    <p className='font-semibold'>
                      <Trans>Address</Trans>
                    </p>
                  </div>
                  {bookingPermissions?.canEditAddress && (
                    <NoddiDummyButton startIcon='Edit' variant='outline' size='extraSmall'>
                      <Trans>Edit</Trans>
                    </NoddiDummyButton>
                  )}
                </div>

                <p>{getLongAddressName(booking.address)}</p>
                {!bookingPermissions?.canEditAddress && (
                  <p className='text-muted-foreground'>
                    <Trans>It is too late to change the address now</Trans>
                  </p>
                )}
              </div>
            </NoddiBasicCard>
          )}
          <NoddiBasicCard
            {...(bookingPermissions?.timeWindows.canEdit && {
              // oxlint-disable-next-line typescript/no-misused-promises -- NoddiBasicCard onClick is () => void; navigate() returns a promise that only tracks lazy-route loading
              onClick: () => navigate(customerRoutes.editMyBookingTimeWindow.getPath({ slug: booking.slug }))
            })}
          >
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-2'>
                  <NoddiIcon name='Calendar' />
                  <p className='font-semibold'>
                    <Trans>Time window</Trans>
                  </p>
                </div>
                {bookingPermissions?.timeWindows.canEdit && (
                  <NoddiDummyButton startIcon='Edit' variant='outline' size='extraSmall'>
                    <Trans>Edit</Trans>
                  </NoddiDummyButton>
                )}
              </div>

              <p>
                {deliveryDate}, {getBookingTimeDisplayText(booking)}
              </p>
              {!bookingPermissions?.timeWindows.canEdit && (
                <p className='text-muted-foreground'>
                  <Trans>We are sorry, but you can't change the time window now</Trans>
                </p>
              )}
            </div>
          </NoddiBasicCard>
          <NoddiAsyncConfirmationDialog
            title={t`Cancel booking`}
            cancelText={t`Back`}
            confirmText={t`Cancel booking`}
            description={
              isStationary
                ? t`Canceling the booking is completely free of charge. Cancellation is available until 24h before the booking.`
                : t`Canceling the booking is completely free of charge. Cancellation is available until 18:00 the day before the booking date.`
            }
            trigger={
              <NoddiButton variant='destructive' className='w-full' startIcon='Trash2'>
                {t`Cancel booking`}
              </NoddiButton>
            }
            confirmAction={async () => {
              await cancelBooking({
                bookingId: booking.id,
                notifyCustomer: true,
                cancellationReason: cancellationReason.trim()
              });
            }}
            confirmActionLoading={isCancelling}
          >
            <NoddiTextArea
              label={t`Reason for cancellation (optional)`}
              value={cancellationReason}
              onChange={(event) => setCancellationReason(event.target.value)}
              placeholder={t`Tell us why you are cancelling (optional)`}
              rows={3}
            />
            {hasPaidWithPayBefore && (
              <p className='text-sm text-muted-foreground'>
                <Trans>
                  Your payment will be refunded. You can expect to see the money back in your account within 2-3
                  business days.
                </Trans>
              </p>
            )}
          </NoddiAsyncConfirmationDialog>
        </div>
      </div>

      <CustomerLoginModal
        open={!isLoggedIn}
        onClose={() => {}} // Prevent closing when not logged in
        title={t`Sign in to change your booking`}
        description={t`Please sign in to access booking management options`}
      />
    </ContentWrapperCustomerApp>
  );
};
